import { useCallback, useMemo, useState } from 'react'
import type {
  ApprovalOption,
  ApprovalType,
  ComparisonTab,
  GuidanceType,
} from '../components/ComparisonTabs'
import { GridViewType } from '../components/GridViewToggle'
import { PL_GUIDANCE_TYPES } from '../constants'
import {
  assignColorSlot,
  freeColorSlot,
  isColorLimitReached,
  resolveColorMap,
  type ColumnKey,
  type DynamicColorMap,
} from '../utils/dynamicColorMap'

interface UseDataPointSelectionsResult {
  currentActiveTabs: ComparisonTab[]
  currentApprovalSelections: ApprovalType[]
  currentGuidanceSelections: GuidanceType[]
  currentColorMap: ReturnType<typeof resolveColorMap>
  currentIsLimitReached: boolean
  plActiveTabs: ComparisonTab[]
  pnActiveTabs: ComparisonTab[]
  sanitizedPlApprovalSelections: ApprovalType[]
  sanitizedPnApprovalSelections: ApprovalType[]
  plGuidanceSelections: GuidanceType[]
  pnGuidanceSelections: GuidanceType[]
  plResolvedColorMap: ReturnType<typeof resolveColorMap>
  pnResolvedColorMap: ReturnType<typeof resolveColorMap>
  isSelectionsEmpty: boolean
  handleTabsChange: (tabs: ComparisonTab[]) => void
  handleGuidanceTypesChange: (types: GuidanceType[]) => void
  handleApprovalTypesChange: (types: ApprovalType[]) => void
  resetSelections: () => void
}

const updateColorMap = (
  colorMap: DynamicColorMap,
  added: ColumnKey | undefined,
  removed: ColumnKey | undefined
): DynamicColorMap | null => {
  let updated = colorMap
  if (added) {
    updated = assignColorSlot(updated, added)
    if (updated[added] === undefined) return null
  }
  if (removed) {
    updated = freeColorSlot(updated, removed)
  }
  return updated
}

export const useDataPointSelections = (
  gridView: GridViewType,
  approvalOptions: ApprovalOption[] | undefined
): UseDataPointSelectionsResult => {
  const [plActiveTabs, setPlActiveTabs] = useState<ComparisonTab[]>([])
  const [pnActiveTabs, setPnActiveTabs] = useState<ComparisonTab[]>([])
  const [plApprovalSelections, setPlApprovalSelections] = useState<ApprovalType[]>([])
  const [pnApprovalSelections, setPnApprovalSelections] = useState<ApprovalType[]>([])
  const [plGuidanceSelections, setPlGuidanceSelections] = useState<GuidanceType[]>([])
  const [pnGuidanceSelections, setPnGuidanceSelections] = useState<GuidanceType[]>([])
  const [plColorMap, setPlColorMap] = useState<DynamicColorMap>({})
  const [pnColorMap, setPnColorMap] = useState<DynamicColorMap>({})

  const allowedApprovalIds = useMemo(() => approvalOptions?.map((o) => o.id), [approvalOptions])

  const sanitizeApprovalSelections = useCallback(
    (types: ApprovalType[]): ApprovalType[] =>
      allowedApprovalIds ? types.filter((type) => allowedApprovalIds.includes(type)) : types,
    [allowedApprovalIds]
  )

  const sanitizedPlApprovalSelections = useMemo(
    () => sanitizeApprovalSelections(plApprovalSelections),
    [plApprovalSelections, sanitizeApprovalSelections]
  )

  const sanitizedPnApprovalSelections = useMemo(
    () => sanitizeApprovalSelections(pnApprovalSelections),
    [pnApprovalSelections, sanitizeApprovalSelections]
  )

  const currentActiveTabs = gridView === 'PL' ? plActiveTabs : pnActiveTabs
  const currentApprovalSelections =
    gridView === 'PL' ? sanitizedPlApprovalSelections : sanitizedPnApprovalSelections
  const currentGuidanceSelections = gridView === 'PL' ? plGuidanceSelections : pnGuidanceSelections

  const currentColorMap = useMemo(
    () => resolveColorMap(gridView === 'PL' ? plColorMap : pnColorMap),
    [gridView, plColorMap, pnColorMap]
  )

  const currentIsLimitReached = isColorLimitReached(gridView === 'PL' ? plColorMap : pnColorMap)

  const plResolvedColorMap = useMemo(() => resolveColorMap(plColorMap), [plColorMap])
  const pnResolvedColorMap = useMemo(() => resolveColorMap(pnColorMap), [pnColorMap])

  const isSelectionsEmpty =
    plActiveTabs.length === 0 &&
    pnActiveTabs.length === 0 &&
    sanitizedPlApprovalSelections.length === 0 &&
    sanitizedPnApprovalSelections.length === 0 &&
    plGuidanceSelections.length === 0 &&
    pnGuidanceSelections.length === 0 &&
    Object.keys(plColorMap).length === 0 &&
    Object.keys(pnColorMap).length === 0

  const handleTabsChange = useCallback(
    (tabs: ComparisonTab[]) => {
      const prevTabs = gridView === 'PL' ? plActiveTabs : pnActiveTabs
      const colorMap = gridView === 'PL' ? plColorMap : pnColorMap
      const setColorMap = gridView === 'PL' ? setPlColorMap : setPnColorMap
      const setTabs = gridView === 'PL' ? setPlActiveTabs : setPnActiveTabs

      const added = tabs.find((t) => t === 'requestedPrice' && !prevTabs.includes(t)) as
        | ColumnKey
        | undefined
      const removed = prevTabs.find((t) => t === 'requestedPrice' && !tabs.includes(t)) as
        | ColumnKey
        | undefined

      const updatedColorMap = updateColorMap(colorMap, added, removed)
      if (updatedColorMap === null) return

      setColorMap(updatedColorMap)
      setTabs(tabs)
    },
    [gridView, plActiveTabs, pnActiveTabs, plColorMap, pnColorMap]
  )

  const handleGuidanceTypesChange = useCallback(
    (types: GuidanceType[]) => {
      const allowedGuidanceTypes = gridView === 'PL' ? PL_GUIDANCE_TYPES : undefined
      const sanitizedTypes = allowedGuidanceTypes
        ? types.filter((type) => allowedGuidanceTypes.includes(type))
        : types

      const prevTypes = gridView === 'PL' ? plGuidanceSelections : pnGuidanceSelections
      const colorMap = gridView === 'PL' ? plColorMap : pnColorMap
      const setColorMap = gridView === 'PL' ? setPlColorMap : setPnColorMap
      const setSelections = gridView === 'PL' ? setPlGuidanceSelections : setPnGuidanceSelections

      const added = sanitizedTypes.find((t) => !prevTypes.includes(t))
      const removed = prevTypes.find((t) => !sanitizedTypes.includes(t))

      const updatedColorMap = updateColorMap(colorMap, added, removed)
      if (updatedColorMap === null) return

      setColorMap(updatedColorMap)
      setSelections(sanitizedTypes)
    },
    [gridView, plGuidanceSelections, pnGuidanceSelections, plColorMap, pnColorMap]
  )

  const handleApprovalTypesChange = useCallback(
    (types: ApprovalType[]) => {
      const sanitizedTypes = sanitizeApprovalSelections(types)

      const prevTypes =
        gridView === 'PL' ? sanitizedPlApprovalSelections : sanitizedPnApprovalSelections
      const colorMap = gridView === 'PL' ? plColorMap : pnColorMap
      const setColorMap = gridView === 'PL' ? setPlColorMap : setPnColorMap
      const setSelections = gridView === 'PL' ? setPlApprovalSelections : setPnApprovalSelections

      const added = sanitizedTypes.find((t) => !prevTypes.includes(t))
      const removed = prevTypes.find((t) => !sanitizedTypes.includes(t))

      const updatedColorMap = updateColorMap(colorMap, added, removed)
      if (updatedColorMap === null) return

      setColorMap(updatedColorMap)
      setSelections(sanitizedTypes)
    },
    [
      gridView,
      sanitizeApprovalSelections,
      sanitizedPlApprovalSelections,
      sanitizedPnApprovalSelections,
      plColorMap,
      pnColorMap,
    ]
  )

  const resetSelections = useCallback(() => {
    setPlActiveTabs([])
    setPnActiveTabs([])
    setPlApprovalSelections([])
    setPnApprovalSelections([])
    setPlGuidanceSelections([])
    setPnGuidanceSelections([])
    setPlColorMap({})
    setPnColorMap({})
  }, [])

  return {
    currentActiveTabs,
    currentApprovalSelections,
    currentGuidanceSelections,
    currentColorMap,
    currentIsLimitReached,
    plActiveTabs,
    pnActiveTabs,
    sanitizedPlApprovalSelections,
    sanitizedPnApprovalSelections,
    plGuidanceSelections,
    pnGuidanceSelections,
    plResolvedColorMap,
    pnResolvedColorMap,
    isSelectionsEmpty,
    handleTabsChange,
    handleGuidanceTypesChange,
    handleApprovalTypesChange,
    resetSelections,
  }
}
